import { Table } from "@components/common/table/Table";
import { PeekAlert, PeekBox, PeekButton, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { ScanRequestStatus, useAnalytics, useScanRequests } from "@piiqtechnologies/ui-lib";
import { useAuthContext } from "@stores/AuthContext";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useOutletContext } from "react-router-dom";
import { ScanRequestActionCell } from "../../utils";
import messages from "./messages.i18n";
import cancelMessages from "../../upcoming-shoots/messages.i18n";
import CancelDialog from "../../upcoming-shoots/cancel-dialog/CancelDialog";
import CancelFields from "../../upcoming-shoots/photoshoot-details/CancelFields";
import { DefaultScanRequestStatus, ScanRequestsConfig, buildInitialNotesAndReasons, getDefaultCancellationReason, } from "./ScanRequestsTableConfig";
export const ScanRequestsTable = ({ isScheduleMode, onSelectScanRequest, communitySelected, organizationSelected, isAdminTable, searchKey, enableCancelSelection, }) => {
    var _a;
    const intl = useIntl();
    const theme = useTheme();
    const { statusFilter } = useOutletContext();
    const { isAdmin } = useAuthContext();
    const analytics = useAnalytics();
    const [appliedFilters, setAppliedFilters] = useState({
        status: isAdminTable
            ? (statusFilter === null || statusFilter === void 0 ? void 0 : statusFilter.length)
                ? statusFilter
                : DefaultScanRequestStatus
            : [ScanRequestStatus.REQUESTED],
    });
    const scanRequestsAPI = useScanRequests();
    const { scanRequestsParams, scanRequestsColumns, sortPredicate } = ScanRequestsConfig({
        communitySelected: communitySelected,
        isAdminTable: isAdminTable,
        scanRequestsAPI: scanRequestsAPI,
        debouncedSearchKey: searchKey,
        statusFilter: appliedFilters.status,
    });
    const handleSelectScanRequest = (tableState) => {
        const rows = tableState.selectedRows;
        onSelectScanRequest === null || onSelectScanRequest === void 0 ? void 0 : onSelectScanRequest(rows.map(({ _id, space }) => ({ _id, space: space.unit, organization: organizationSelected })));
    };
    // Cancellation selection state (only used when enableCancelSelection is true)
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [notesAndReasonsByRequestId, setNotesAndReasonsByRequestId] = useState({});
    const updatedRequestsArray = useMemo(() => {
        var _a;
        const data = ((_a = scanRequestsAPI === null || scanRequestsAPI === void 0 ? void 0 : scanRequestsAPI[1]) === null || _a === void 0 ? void 0 : _a.data) || [];
        return (Object.keys(notesAndReasonsByRequestId).map((requestId) => {
            const currentRequest = data.find((request) => request._id === requestId);
            const reasonData = notesAndReasonsByRequestId[requestId];
            return Object.assign(Object.assign({}, currentRequest), { reason: reasonData === null || reasonData === void 0 ? void 0 : reasonData.reason, subReason: reasonData === null || reasonData === void 0 ? void 0 : reasonData.subReason, comment: reasonData === null || reasonData === void 0 ? void 0 : reasonData.comment });
        }) || []);
    }, [notesAndReasonsByRequestId, (_a = scanRequestsAPI === null || scanRequestsAPI === void 0 ? void 0 : scanRequestsAPI[1]) === null || _a === void 0 ? void 0 : _a.data]);
    const handleCancelSelectionChange = (newSelection) => {
        if (newSelection.selectedRows.length === 0) {
            setNotesAndReasonsByRequestId({});
            setValidationError("");
            return;
        }
        const next = buildInitialNotesAndReasons(newSelection.selectedRows, notesAndReasonsByRequestId, intl);
        setNotesAndReasonsByRequestId(next);
    };
    const handleUpdateNotesAndReasons = (scanRequestId, comment, reason, subReason) => {
        var _a, _b, _c;
        setNotesAndReasonsByRequestId(Object.assign(Object.assign({}, notesAndReasonsByRequestId), { [scanRequestId]: {
                comment: comment !== undefined ? comment : ((_a = notesAndReasonsByRequestId[scanRequestId]) === null || _a === void 0 ? void 0 : _a.comment) || "",
                reason: reason || ((_b = notesAndReasonsByRequestId[scanRequestId]) === null || _b === void 0 ? void 0 : _b.reason) || getDefaultCancellationReason(intl),
                subReason: subReason !== undefined ? subReason : ((_c = notesAndReasonsByRequestId[scanRequestId]) === null || _c === void 0 ? void 0 : _c.subReason) || "",
            } }));
        // Clear validation error when user makes changes
        setValidationError("");
    };
    if (!communitySelected) {
        return (React.createElement(PeekAlert, null,
            React.createElement(PeekTypography, null, intl.formatMessage(messages.scanRequestsSelectCommunity))));
    }
    return (React.createElement(PeekBox, { sx: {
            "& .MuiTableContainer-root": {
                scrollbarWidth: "thin !important",
                maxHeight: isAdmin ? "none" : "calc(100vh - 236px)",
                overflowY: "auto",
                boxShadow: "none !important",
            },
        } },
        enableCancelSelection && (React.createElement(PeekStack, { direction: "row", width: "100%", justifyContent: "flex-end", sx: { mt: theme.spacing(1), mb: theme.spacing(1) } },
            React.createElement(PeekStack, { direction: "column", spacing: 1, width: "100%", alignItems: "flex-end" },
                validationError && (React.createElement(PeekTypography, { variant: "body2", color: theme.palette.error.main }, validationError)),
                React.createElement(PeekButton, { variant: "outlined", disabled: Object.keys(notesAndReasonsByRequestId).length === 0, onClick: () => {
                        // Validate before opening dialog
                        const otherReasonText = intl.formatMessage(cancelMessages.reasonOther);
                        const hasInvalidRequests = updatedRequestsArray.some((request) => {
                            const isOtherSubReason = request.subReason === otherReasonText;
                            const hasNoComment = !request.comment || request.comment.trim() === "";
                            return isOtherSubReason && hasNoComment;
                        });
                        if (hasInvalidRequests) {
                            setValidationError('Please provide notes for requests with "Other" as the reason.');
                            return;
                        }
                        setValidationError("");
                        analytics.track("Scan Request - Cancel Selected", {
                            scanRequestsSelected: updatedRequestsArray.map((scan) => scan._id).join(", "),
                        });
                        setShowCancelDialog(true);
                    }, sx: {
                        color: theme.palette.error.main,
                        borderColor: theme.palette.grey[200],
                        ":hover": {
                            borderColor: theme.palette.grey[300],
                        },
                    } }, intl.formatMessage(cancelMessages.cancelSelectedButton))))),
        React.createElement(Table, { api: scanRequestsAPI, apiConfig: {
                params: scanRequestsParams,
                serverSidePagination: false,
                serverSideSorting: false,
            }, canSelectRow: enableCancelSelection
                ? (row) => row.status === ScanRequestStatus.SCHEDULED || row.status === ScanRequestStatus.REQUESTED
                : undefined, onFiltersChange: (tableState) => {
                var _a, _b;
                setAppliedFilters(((_b = (_a = tableState.filters) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.length)
                    ? tableState.filters
                    : {
                        status: isAdminTable
                            ? (statusFilter === null || statusFilter === void 0 ? void 0 : statusFilter.length)
                                ? statusFilter
                                : DefaultScanRequestStatus
                            : [ScanRequestStatus.REQUESTED],
                    });
            }, onSelectionChange: (isScheduleMode && handleSelectScanRequest) ||
                (enableCancelSelection ? (newSelection) => handleCancelSelectionChange(newSelection) : undefined), columns: scanRequestsColumns, actions: (row) => [React.createElement(ScanRequestActionCell, { key: `${row._id}_action`, scanRequest: row })], actionsCover: (row) => [React.createElement(ScanRequestActionCell, { key: `${row._id}_cover`, scanRequest: row })], notFoundMessage: messages.noScanRequests, 
            // @ts-ignore
            sortPredicate: sortPredicate, rowSelectedContent: enableCancelSelection
                ? (row) => (React.createElement(CancelFields, { row: row, notesAndReasonsByRequestId: notesAndReasonsByRequestId, onNotesChanged: (scanRequestId, comment) => handleUpdateNotesAndReasons(scanRequestId, comment), onReasonSelected: (scanRequestId, reason, subReason) => handleUpdateNotesAndReasons(scanRequestId, undefined, reason, subReason), isAdmin: isAdmin }))
                : undefined }),
        enableCancelSelection && showCancelDialog && (React.createElement(CancelDialog, { scanRequests: updatedRequestsArray, onClose: () => setShowCancelDialog(false), showAllSpacesAlert: false }))));
};
export default ScanRequestsTable;
